import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import {
  useCandidateProfileById,
  usePatchCandidateProfile,
} from "./hooks/useCandidateProfile";

import { EditorHeader } from "./components/editor/EditorHeader";
import {
  EditorSidebar,
  type EditorTabType,
} from "./components/editor/EditorSidebar";
import { PersonalInfoSection } from "./components/editor/PersonalInfoSection";
import { ProfessionalInfoSection } from "./components/editor/ProfessionalInfoSection";
import { ExperienceSection } from "./components/editor/ExperienceSection";
import { SkillsSection } from "./components/editor/SkillsSection";
import { ProjectsSection } from "./components/editor/ProjectsSection";
import { EducationSection } from "./components/editor/EducationSection";
import { CertificationsSection } from "./components/editor/CertificationsSection";
import { AwardsSection } from "./components/editor/AwardsSection";
import { SummarySection } from "./components/editor/SummarySection";

export default function CandidateProfileEditor() {
  const { id } = useParams<{ id?: string }>();
  const { data: user } = useCurrentUser();
  const [activeTab, setActiveTab] = useState<EditorTabType>(
    "Personal Information",
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const effectiveId = id || user?.id;

  const { data: profileRes, isLoading: isProfileLoading } =
    useCandidateProfileById(effectiveId, Boolean(effectiveId));

  const profile = profileRes?.data;
  const patchProfileMutation = usePatchCandidateProfile();

  if (isProfileLoading) {
    return (
      <div className="flex justify-center items-center min-h-[600px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const fullName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
    profile.username
    : "Candidate Profile";

  const profileBackUrl = id ? `/candidates/${id}` : "/profile";

  const renderActiveTab = () => {
    switch (activeTab) {
      case "Personal Information":
        return (
          <PersonalInfoSection
            profile={profile}
            patchProfile={patchProfileMutation}
          />
        );
      case "Professional Information":
        return (
          <ProfessionalInfoSection
            profile={profile}
            patchProfile={patchProfileMutation}
          />
        );
      case "Experience":
        return <ExperienceSection profileId={profile?.id} profile={profile} />;
      case "Skills":
        return <SkillsSection profileId={profile?.id} />;
      case "Projects":
        return <ProjectsSection profileId={profile?.id} />;
      case "Education":
        return <EducationSection profileId={profile?.id} />;
      case "Certifications":
        return <CertificationsSection profileId={profile?.id} />;
      case "Awards":
        return <AwardsSection profileId={profile?.id} />;
      case "Summary":
        return (
          <SummarySection
            profile={profile}
            patchProfile={patchProfileMutation}
          />
        );
      default:
        return (
          <PersonalInfoSection
            profile={profile}
            patchProfile={patchProfileMutation}
          />
        );
    }
  };

  return (


    <div className="relative z-10 flex flex-col gap-6 p-6">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm font-medium text-slate-500">
        <Link
          to="/candidates"
          className="hover:text-primary transition-colors"
        >
          Candidates
        </Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <Link
          to={profileBackUrl}
          className="hover:text-primary transition-colors"
        >
          {fullName}
        </Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="text-slate-900 dark:text-white">Edit Profile</span>
      </div>

      {/* Hero Header Component */}
      <EditorHeader profile={profile} profileBackUrl={profileBackUrl} />

      {/* Main Content Layout */}
      <div className="flex gap-6 items-start h-[calc(100vh-280px)] min-h-[600px]">
        {/* Left Sidebar Menu Component */}
        <EditorSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
        />

        {/* Right Section Content */}
        <div className="flex-1 overflow-y-auto h-full pr-2">
          {renderActiveTab()}
        </div>
      </div>
    </div>
  );
}
